import {
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeftBar, IconTool } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createOrganizationSchema,
} from "../../seo/utils";

type MathToolPlaceholderPageProps = {
  title: string;
  description: string;
  path: string;
};

const TOOLS_PATH = "/tools";

function MathToolPlaceholderPage({
  title,
  description,
  path,
}: MathToolPlaceholderPageProps) {
  const navigate = useNavigate();

  return (
    <>
      <Seo
        title={`${title} | SubjectBuddy`}
        description={description}
        path={path}
        noIndex
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: title, path },
          ]),
        ]}
      />

      <Box
        style={{
          background:
            "linear-gradient(135deg, rgba(255, 244, 230, 0.95) 0%, #ffffff 58%, rgba(238, 246, 255, 0.95) 100%)",
          borderBottom: "1px solid #f3e4d2",
        }}
      >
        <Container size="xl" py={64}>
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>{title}</Text>
            </Breadcrumbs>

            <Button
              color="orange"
              variant="subtle"
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate(TOOLS_PATH)}
              style={{ width: "fit-content", paddingLeft: 0 }}
            >
              Back to Tools
            </Button>

            <Paper withBorder radius="xl" p="xl">
              <Stack gap="md" maw={720}>
                <Group gap="sm">
                  <Badge color="orange" size="lg" radius="sm">
                    Math Tool
                  </Badge>
                  <Badge color="blue" size="lg" radius="sm" variant="light">
                    Tool Page
                  </Badge>
                </Group>
                <Group gap="sm">
                  <IconTool size={28} color="#d97706" />
                  <Title order={1}>{title}</Title>
                </Group>
                <Text c="dimmed" size="lg" style={{ lineHeight: 1.8 }}>
                  {description}
                </Text>
                <Text c="dimmed">
                  This calculator page is available as a crawlable placeholder
                  so students can browse related math tools from the racine
                  carrée calculator and tools hub.
                </Text>
                <Group gap="md">
                  <Button component={Link} to="/tools/racine-carree-calculator" color="orange">
                    Open Racine Carrée Calculator
                  </Button>
                  <Button component={Link} to={TOOLS_PATH} variant="light" color="blue">
                    View All Tools
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </>
  );
}

export default MathToolPlaceholderPage;
